// MPD-68 — the Overview supplier share card follows the supplier's MAIN segment.
//
// The card was hard-wired to All White, so a tobacco-, vape- or nicotine-free-only supplier read
// "X's CU as % of all All White CU" = 0 % or nothing. The API now picks the supplier's largest
// segment by CU in the month, within its permitted segments (own_main_segment). This module turns
// that into the card's texts: a short visible basis (MP-OS-187), the full definition and the reason
// for the segment in the ⓘ, and an honest empty state instead of a stated 0 %.
//
// Pure so the rule is unit-tested without rendering the card.

import type { MainSegment } from './landing'
import { segmentFromApi, segmentLabel } from './segments'

export interface MainSegmentShareText {
  segment: string | null // display label, e.g. 'Tobacco snus'; null = no segment to show
  label: string
  labelTip: string
  value: number | null // the share for the chosen period; null = nothing to state
  empty: string | null // shown instead of a figure when value is null
  chosenNote: string | null // visible when the segment had to be chosen from all time
  marketNote: string // appended to brand-share definitions
  marketScope: SegmentMarketScope | null // the segment market's scope, for the monthly report
}

/** The ONE wording for what a supplier's segment market covers (MPD-73 #3): the share card's ⓘ
 *  (`note`) and the monthly report's market row and brand-share heading (`short`) both use it.
 *  Vape and Smoking are scoped to the supplier's own product types (the Top lists rule), so a
 *  pod-only supplier's market is pods, not the whole segment. */
export interface SegmentMarketScope {
  note: string // appended to a share definition; '' for a whole segment
  short: string // the report's market row note
  narrowed: boolean // true when only the supplier's product types count
}

export function segmentMarketScope(apiSegment: string, scope: string): SegmentMarketScope {
  // MPD-81 S4: "permitted" = a filtered market with no segment, narrowed to every permitted row.
  if (apiSegment === 'permitted') {
    return { note: ` Only the segments and product types in ${scope}’s assortment count.`,
      short: `only the segments and product types in ${scope}’s assortment`, narrowed: true }
  }
  const segment = mainSegmentLabel(apiSegment)
  return apiSegment === 'Vape' || apiSegment === 'Smoking'
    ? { note: ` Only the ${segment} product types in ${scope}’s assortment count.`,
        short: `only ${segment} product types in ${scope}’s assortment`, narrowed: true }
    : { note: '', short: 'whole segment', narrowed: false }
}

/** The display label for an API segment ('Tobacco' → 'Tobacco snus'), the one the rest of the app uses. */
export function mainSegmentLabel(segment: string): string {
  const seg = segmentFromApi(segment)
  return seg ? segmentLabel(seg) : segment
}

export function mainSegmentShareText(args: {
  main: MainSegment | null
  scope: string // the supplier's display name
  month: string // the snapshot's month label, e.g. 'September 2026'
  allTime: boolean // the card's period toggle
}): MainSegmentShareText {
  const { main, scope, month, allTime } = args
  const periodPhrase = allTime ? 'all time' : month
  // MPD-73 #5: CU sold can be above zero while the share card is empty — sell-off of discontinued
  // articles counts in CU sold but grants no permitted segment — so the empty state names the
  // permitted segments rather than claiming no sales at all.
  const emptyText = allTime ? 'No sales in its permitted segments yet' : `No sales in its permitted segments in ${month}`
  const discontinued = ' Sales of discontinued articles do not count toward a permitted segment, so CU sold can still be above zero.'
  if (!main) {
    return {
      segment: null,
      label: `Share of segment CU · ${periodPhrase}`,
      labelTip: `${scope} has no CU sold in any of its permitted segments in any period so far, so there is no segment share to show. An empty card is not a share of 0 %.${discontinued}`,
      value: null,
      empty: emptyText,
      chosenNote: null,
      marketNote: '',
      marketScope: null,
    }
  }
  const segment = mainSegmentLabel(main.segment)
  const marketScope = segmentMarketScope(main.segment, scope)
  const marketNote = marketScope.note
  const reason = main.chosen_by === 'alltime'
    ? `${scope} has no sales in its permitted segments in ${month}, so ${segment} is shown as its largest segment by CU over all time.${discontinued}`
    : `${segment} is shown because it is ${scope}’s largest segment by CU in ${month}.`
  const value = allTime ? main.share_pct_alltime : main.share_pct
  return {
    segment,
    label: `Share of ${segment} CU · ${periodPhrase}`,
    labelTip: `${scope}’s CU as a percentage of all ${segment} CU sold in the market, for the period chosen with the buttons (all time or the selected month). ${reason}${marketNote}`,
    value,
    empty: value != null ? null : emptyText,
    chosenNote: main.chosen_by === 'alltime' ? `${segment} chosen from all time` : null,
    marketNote,
    marketScope,
  }
}

/** MP-OS-192: the figure the Overview share card is showing, as basis facts (API segment and the
 *  'YYYY-MM' month), so another view can tell whether it would repeat it. null = the card states
 *  no monthly figure: all time is selected, or there is no main segment or no sale in the month. */
export interface ShareCardFigure {
  segment: string
  month: string
}

export function shareCardFigure(args: { main: MainSegment | null; month: string; allTime: boolean }): ShareCardFigure | null {
  const { main, month, allTime } = args
  if (allTime || !main || main.chosen_by !== 'month' || main.share_pct == null) return null
  return { segment: main.segment, month }
}

// The segment-view row whose share has the same basis as the card's segment share: own CU ÷ the
// segment's permitted market CU. Only the snus segments qualify: the card's Vape/Smoking market
// is the whole category within the supplier's product types, while a row is one product type,
// and the segment view's "Other" is the untracked residual, not the main-segment fallback.
const ROW_FOR_CARD_SEGMENT: Record<string, string> = {
  'All White': 'All White',
  Tobacco: 'Tobakssnus',
  'Nicotine Free': 'Nikotinfritt',
}

/** The segment-view row (its API leaf) whose % the share card already shows for this month, or
 *  null when nothing is repeated. `wholeMarket` rows are a composition, never the card's share. */
export function segmentRowOnShareCard(card: ShareCardFigure | null, month: string, wholeMarket: boolean): string | null {
  if (!card || wholeMarket || card.month !== month) return null
  return ROW_FOR_CARD_SEGMENT[card.segment] ?? null
}
